/** @forge-header
 * @id          valueDashboardView
 * @role        Multi-sport value dashboard | sport/market/date selection → game tables → trade modal
 * @pattern     component
 * @tier        T3
 * @purity      dom | localStorage
 * @units       cents | dollars
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    dateUtils:getTodayStr,findNearestAvailableDate,isGameLikelyOver
 *              | sportsDiscovery:Sport,MarketType,SPORT_CONFIGS,getSportConfig
 *              | sportsStream:GameData,SportsLoadingState,MoneylineGameData,SpreadGameData,TotalGameData,moneylineToConsolidated
 *              | nbaConsolidated/types:ConsolidatedGameBooks
 *              | nbaConsolidated/pricing:getTeamWinData,liquidityDollars
 *              | nbaConsolidated/volumeSettings:VolumeWindow,initVolumeSettings,toggleWindow
 *              | nbaConsolidated/viewModeSettings:getViewMode,setViewMode
 *              | hooks/useRotos:useRotos | hooks/useSamplePositions:useSamplePositions
 *              | lib/hotkeys:useHotkeys
 * @downstream  App.tsx (lazy-loaded page route)
 * @exports     ValueDashboardView:component
 * @test        untested (integration-level)
 * @state       selectedSport:Sport | selectedMarketType:MarketType | selectedDate:string|null
 *              | oddsMode:OddsMode | viewMode:ViewMode | expandedGameKeys:Set<string>
 *              | tradeReq:TradeClickRequest|null | gameFilter:'active'|'all'|'settled'
 *              | volumeWindows:VolumeWindow[] | tableCollapsed:boolean | tableCompact:boolean
 * @lifecycle   init (localStorage volume/viewMode) -> sport/market selection -> date filtering
 *              -> game rendering (moneyline|spread|total) -> trade modal submission
 * @invariant   date-snap guard | must not snap while loading — partial availableDates can mis-snap
 * @danger      effectiveSportsGames merges test data by Map key — stale keys cause phantom games
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useHotkeys } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import { getTodayStr, findNearestAvailableDate, isGameLikelyOver } from '@/lib/dateUtils';
import { type Sport, type MarketType, SPORT_CONFIGS, getSportConfig } from '@/lib/sportsDiscovery';
import type { GameData, SportsLoadingState } from '@/lib/sportsStream';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import { useRotos } from '@/hooks/useRotos';
import { useSamplePositions } from '@/hooks/useSamplePositions';
import type { generateMockTraderState } from '@/mock/trader';
import type { NBAMarketRow, Position } from '@/types';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { PolyOpenOrder } from '@/lib/polymarket/types';
import type { VelocityResult } from '@/lib/queueVelocity';

// Existing NBA components
import {
  DashboardHeader,
  type OddsMode,
  type ViewMode,
} from '@/components/nba-value-dashboard/DashboardHeader';
import {
  ConsolidatedMoneylines,
  type TradeClickRequest,
} from '@/components/nba-value-dashboard/ConsolidatedMoneylines';
import { TwoRowMoneylines } from '@/components/nba-value-dashboard/TwoRowMoneylines';
import { LegacyOpenOrdersPanel } from '@/components/nba-value-dashboard/OpenOrdersPanel';
import { LegacyOpenPositionsPanel } from '@/components/nba-value-dashboard/OpenPositionsPanel';
import { LegacyCombinedOrdersPositionsPanel } from '@/components/nba-value-dashboard/CombinedOrdersPositionsPanel';
import { GameAccordion } from '@/components/nba-value-dashboard/GameAccordion';
import {
  PlaceOrderModal,
  type PlaceOrderRequest,
  type PlaceOrderSubmission,
} from '@/components/nba-value-dashboard/PlaceOrderModal';
import { GameDetailPanel } from '@/components/nba-value-dashboard/GameDetailPanel';
import { getTeamWinData, liquidityDollars } from '@/lib/nbaConsolidated/pricing';
import { resolveDisplayTitle } from '@/lib/kalshiTicker/displayTitle';
import { useToast } from '@/components/organisms/Toast';
import {
  type VolumeWindow,
  initVolumeSettings,
  toggleWindow,
} from '@/lib/nbaConsolidated/volumeSettings';
import {
  getViewMode,
  setViewMode as persistViewMode,
} from '@/lib/nbaConsolidated/viewModeSettings';

// New sports components
import { TwoRowTotals, TwoRowSpreads } from '@/components/sports';
import {
  type TotalGameData,
  type SpreadGameData,
  type MoneylineGameData,
  moneylineToConsolidated,
} from '@/lib/sportsStream';

/** Sport tab options */
const SPORT_TABS = SPORT_CONFIGS.map((config) => ({
  value: config.sport,
  label: config.shortLabel,
}));

/** Stable empty defaults to avoid creating new Map() on every render */
const EMPTY_MAP = new Map() as Map<string, never>;
const EMPTY_BOOL_MAP = new Map() as Map<string, boolean>;
const EMPTY_STATE_MAP = new Map() as Map<string, SportsLoadingState>;

/** Market type labels */
const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',
};

interface ValueDashboardViewProps {
  /** NBA markets data from stream */
  markets?: NBAMarketRow[];
  /** Open orders */
  orders?: KalshiOrder[];
  /** Consolidated NBA games (for NBA moneyline view) */
  consolidatedGames?: ConsolidatedGameBooks[];
  consolidatedLoading?: boolean;
  feedStatus?: 'live' | 'offline';
  lastUpdateTime?: string;
  polyOpenOrders?: PolyOpenOrder[];
  polyStatus?: 'disconnected' | 'connected' | 'error';
  /** Velocity data per order ID */
  velocityByOrderId?: Record<string, VelocityResult>;
  /** Absolute queue depth per order ID */
  absoluteQueueByOrderId?: Record<string, number>;
  /** Open positions */
  positions?: Position[];
  /** Optional mapping from ticker -> market title */
  positionTitleByTicker?: Record<string, string>;
  /** Optional mapping from ticker -> market close/expiration time */
  positionGameDateByTicker?: Record<string, string>;
  /** Optional mapping from ticker -> { yes/no subtitle } */
  positionOutcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  /** Handler for canceling an order */
  onCancelOrder?: (orderId: string) => Promise<void>;
  /** Handler for decreasing order quantity (queue-safe) */
  onDecreaseOrder?: (orderId: string, nextQuantity: number) => Promise<void> | void;
  /** Handler for killing all orders */
  onKillAll?: () => void;
  /** Handler for canceling a Polymarket order */
  onCancelPolyOrder?: (orderId: string) => void;
  /** Handler for killing all Polymarket orders */
  onKillAllPolyOrders?: () => void;
  /** Place a Kalshi order */
  onPlaceKalshiOrder?: (args: {
    marketTicker: string;
    side: 'yes' | 'no';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    expirationTs?: number;
  }) => Promise<void>;
  onPlacePolymarketOrder?: (args: {
    tokenId: string;
    conditionId?: string;
    action: 'buy' | 'sell';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    tickSize?: string;
    negRisk?: boolean;
  }) => Promise<void>;
  /** Multi-sport games data (keyed by "{sport}-{marketType}") */
  sportsGames?: Map<string, GameData[]>;
  sportsLoading?: Map<string, boolean>;
  sportsLoadingStates?: Map<string, SportsLoadingState>;
  /** Callback when sport/market type changes */
  onSportMarketChange?: (sport: Sport, marketType: MarketType) => void;
  /** When true, mute price displays to indicate stale/disconnected feed data */
  feedStale?: boolean;
  /** Global test data toggle state */
  testData?: boolean;
  /** Pre-generated mock trader state (shared across views) */
  mockTraderState?: ReturnType<typeof generateMockTraderState>;
}

export function ValueDashboardView({
  markets: _markets,
  orders: _orders,
  consolidatedGames = [],
  consolidatedLoading = false,
  feedStatus: _feedStatus = 'offline',
  lastUpdateTime: _lastUpdateTime = '--:--:--',
  polyOpenOrders: _polyOpenOrders = [],
  polyStatus: _polyStatus = 'disconnected',
  velocityByOrderId: _velocityByOrderId,
  absoluteQueueByOrderId: _absoluteQueueByOrderId,
  positions,
  positionTitleByTicker,
  positionGameDateByTicker,
  positionOutcomeByTicker,
  onCancelOrder,
  onDecreaseOrder: _onDecreaseOrder,
  onKillAll: _onKillAll,
  onCancelPolyOrder: _onCancelPolyOrder,
  onKillAllPolyOrders: _onKillAllPolyOrders,
  onPlaceKalshiOrder,
  onPlacePolymarketOrder,
  sportsGames = EMPTY_MAP,
  sportsLoading = EMPTY_BOOL_MAP,
  sportsLoadingStates = EMPTY_STATE_MAP,
  onSportMarketChange,
  feedStale = false,
  testData = false,
  mockTraderState,
}: ValueDashboardViewProps) {
  const { addToast } = useToast();

  // Sport and market type selection
  const [selectedSport, setSelectedSport] = useState<Sport>('nba');
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>('moneyline');

  // Date filter — null means "all dates"
  const [selectedDate, setSelectedDate] = useState<string | null>(() => getTodayStr());

  // Existing NBA dashboard state
  const [oddsMode, setOddsMode] = useState<OddsMode>('maker');
  const [viewMode, setViewMode] = useState<ViewMode>(() => getViewMode());
  const [expandedGameKeys, setExpandedGameKeys] = useState<Set<string>>(new Set());
  const [tradeReq, setTradeReq] = useState<TradeClickRequest | null>(null);
  // tradeSubmitting removed — modal closes immediately on submit (F5 non-blocking entry)
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [volumeWindows, setVolumeWindows] = useState<VolumeWindow[]>([]);
  const [tableCollapsed, setTableCollapsed] = useState(false);
  const [tableCompact, setTableCompact] = useState(false);
  const [gameFilter, setGameFilter] = useState<'active' | 'all' | 'settled'>('active');

  // Initialize volume settings from localStorage
  useEffect(() => {
    setVolumeWindows(initVolumeSettings());
  }, []);

  const sample = useSamplePositions(mockTraderState);

  const effectiveOrders = useMemo(
    () => (testData ? [...(_orders ?? []), ...sample.orders] : (_orders ?? [])),
    [testData, _orders, sample.orders]
  );
  const effectivePositions = useMemo(
    () => (testData ? [...(positions ?? []), ...sample.positions] : (positions ?? [])),
    [testData, positions, sample.positions]
  );
  const effectiveTitles = useMemo(
    () =>
      testData ? { ...positionTitleByTicker, ...sample.titleByTicker } : positionTitleByTicker,
    [testData, positionTitleByTicker, sample.titleByTicker]
  );
  const effectiveDates = useMemo(
    () =>
      testData ? { ...positionGameDateByTicker, ...sample.dateByTicker } : positionGameDateByTicker,
    [testData, positionGameDateByTicker, sample.dateByTicker]
  );
  const effectiveOutcomes = useMemo(
    () =>
      testData
        ? { ...positionOutcomeByTicker, ...sample.outcomeByTicker }
        : positionOutcomeByTicker,
    [testData, positionOutcomeByTicker, sample.outcomeByTicker]
  );
  const effectiveConsolidated = useMemo(
    () => (testData ? [...consolidatedGames, ...sample.consolidatedGames] : consolidatedGames),
    [testData, consolidatedGames, sample.consolidatedGames]
  );
  const effectiveSportsGames = useMemo(() => {
    if (!testData) return sportsGames;
    const merged = new Map(sportsGames);
    for (const [key, games] of sample.sportsGames) {
      merged.set(key, [...(merged.get(key) ?? []), ...games]);
    }
    return merged;
  }, [testData, sportsGames, sample.sportsGames]);

  // Get available market types for selected sport
  const availableMarketTypes = useMemo((): MarketType[] => {
    const config = getSportConfig(selectedSport);
    return config?.marketTypes ?? (['moneyline'] as MarketType[]);
  }, [selectedSport]);

  // Ensure selected market type is valid for selected sport
  useEffect(() => {
    if (!availableMarketTypes.includes(selectedMarketType)) {
      const firstType = availableMarketTypes[0];
      if (firstType) {
        setSelectedMarketType(firstType);
      }
    }
  }, [availableMarketTypes, selectedMarketType]);

  // Notify parent when sport/market type changes
  useEffect(() => {
    onSportMarketChange?.(selectedSport, selectedMarketType);
  }, [selectedSport, selectedMarketType, onSportMarketChange]);

  // Keyboard shortcuts for quick navigation (via hotkey system)
  useHotkeys({
    'sport-nba': () => setSelectedSport('nba'),
    'sport-nfl': () => setSelectedSport('nfl'),
    'sport-nhl': () => setSelectedSport('nhl'),
    'sport-cbb': () => setSelectedSport('cbb'),
    'sport-atp': () => setSelectedSport('tennis-atp'),
    'sport-wta': () => setSelectedSport('tennis-wta'),
    'market-moneyline': () => {
      if (availableMarketTypes.includes('moneyline')) {
        setSelectedMarketType('moneyline');
      }
    },
    'market-spread': () => {
      if (availableMarketTypes.includes('spread')) {
        setSelectedMarketType('spread');
      }
    },
    'market-total': () => {
      if (availableMarketTypes.includes('total')) {
        setSelectedMarketType('total');
      }
    },
  });

  // Use moneyline view for all sports now (unified component)
  const useMoneylineView = selectedMarketType === 'moneyline';

  // Get games for current selection
  const currentGamesKey = `${selectedSport}-${selectedMarketType}`;
  const currentGames = sportsGames.get(currentGamesKey) ?? [];
  const currentLoading = sportsLoading.get(currentGamesKey) ?? false;
  const currentLoadingState = sportsLoadingStates.get(currentGamesKey);

  // For non-NBA moneylines, convert to consolidated format
  const convertedMoneylineGames = useMemo(() => {
    if (selectedSport === 'nba' || selectedMarketType !== 'moneyline') {
      return []; // NBA uses consolidatedGames directly
    }
    return (currentGames as MoneylineGameData[]).map(moneylineToConsolidated);
  }, [currentGames, selectedSport, selectedMarketType]);

  // Determine which games to show for moneyline view
  const moneylineGamesRaw = selectedSport === 'nba' ? consolidatedGames : convertedMoneylineGames;
  const moneylineLoading = selectedSport === 'nba' ? consolidatedLoading : currentLoading;

  // Enrich moneyline games with rotation numbers from rotos (Unabated feed via relay)
  const moneylineGamesWithRotation = useRotos(selectedSport, moneylineGamesRaw);

  // Collect unique sorted dates from ALL games (moneyline + current sport games)
  const availableDates = useMemo(() => {
    const dateSet = new Set<string>();
    for (const g of moneylineGamesWithRotation) if (g.date) dateSet.add(g.date);
    for (const g of currentGames) if (g.date) dateSet.add(g.date);
    return Array.from(dateSet).sort();
  }, [moneylineGamesWithRotation, currentGames]);

  // Auto-snap: if selected date has no games, jump to nearest available date.
  // Guard: don't snap while data is still loading — partial availableDates can mis-snap.
  useEffect(() => {
    if (moneylineLoading || currentLoading) return;
    if (!selectedDate || availableDates.length === 0) return;
    const snapped = findNearestAvailableDate(selectedDate, availableDates);
    if (snapped !== null) setSelectedDate(snapped);
  }, [selectedDate, availableDates, moneylineLoading, currentLoading]);

  // Filter games by selected date + completion status (active / all / settled)
  const moneylineGamesForView = useMemo(() => {
    let games = moneylineGamesWithRotation;
    if (selectedDate) {
      games = games.filter((g) => g.date === selectedDate);
    }
    if (gameFilter === 'active') {
      games = games.filter((g) => !isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    } else if (gameFilter === 'settled') {
      games = games.filter((g) => isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    }
    return games;
  }, [moneylineGamesWithRotation, selectedDate, gameFilter, selectedSport]);

  const filteredCurrentGames = useMemo(() => {
    let games = currentGames;
    if (selectedDate) {
      games = games.filter((g) => g.date === selectedDate);
    }
    if (gameFilter === 'active') {
      games = games.filter((g) => !isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    } else if (gameFilter === 'settled') {
      games = games.filter((g) => isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    }
    return games;
  }, [currentGames, selectedDate, gameFilter, selectedSport]);

  // Date navigation
  const handleDatePrev = useCallback(() => {
    if (!selectedDate || availableDates.length === 0) return;
    const idx = availableDates.indexOf(selectedDate);
    if (idx > 0) setSelectedDate(availableDates[idx - 1]!);
  }, [selectedDate, availableDates]);

  const handleDateNext = useCallback(() => {
    if (!selectedDate || availableDates.length === 0) return;
    const idx = availableDates.indexOf(selectedDate);
    if (idx < availableDates.length - 1) setSelectedDate(availableDates[idx + 1]!);
  }, [selectedDate, availableDates]);

  const handleDateToday = useCallback(() => {
    setSelectedDate(getTodayStr());
  }, []);

  const handleDateAll = useCallback(() => {
    setSelectedDate(null);
  }, []);

  // Handlers
  const handleSportChange = useCallback((value: Sport) => {
    setSelectedSport(value);
  }, []);

  const handleMarketTypeChange = useCallback((type: MarketType) => {
    setSelectedMarketType(type);
  }, []);

  const handleToggleVolumeWindow = useCallback((window: VolumeWindow) => {
    setVolumeWindows(toggleWindow(window));
  }, []);

  const handleToggleTableCollapsed = useCallback(() => {
    setTableCollapsed((v) => !v);
  }, []);

  const handleToggleTableCompact = useCallback(() => {
    setTableCompact((v) => !v);
  }, []);

  const handleModeChange = useCallback((mode: OddsMode) => {
    setOddsMode(mode);
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    persistViewMode(mode);
  }, []);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      await onCancelOrder?.(orderId);
    },
    [onCancelOrder]
  );

  const _handleKillAll = useCallback(() => {
    _onKillAll?.();
  }, [_onKillAll]);

  const _handleCancelPolyOrder = useCallback(
    (orderId: string) => {
      _onCancelPolyOrder?.(orderId);
    },
    [_onCancelPolyOrder]
  );

  const _handleKillAllPolyOrders = useCallback(() => {
    _onKillAllPolyOrders?.();
  }, [_onKillAllPolyOrders]);

  const _handleDecreaseOrder = useCallback(
    async (orderId: string, nextQuantity: number) => {
      await _onDecreaseOrder?.(orderId, nextQuantity);
    },
    [_onDecreaseOrder]
  );

  const handleToggleExpand = useCallback((gameKey: string) => {
    setExpandedGameKeys((prev) => {
      if (prev.has(gameKey)) {
        // Deselect: clear all
        return new Set();
      }
      // Single-select: replace with just this game
      return new Set([gameKey]);
    });
  }, []);

  // Map market tickers → game keys so odds cell clicks toggle expansion
  const tickerToGameKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const game of moneylineGamesForView) {
      if (game.kalshi) {
        map.set(game.kalshi.markets.away.marketTicker, game.key);
        map.set(game.kalshi.markets.home.marketTicker, game.key);
      }
      if (game.polymarket) {
        const awayToken = game.polymarket.markets.away.tokenId;
        const homeToken = game.polymarket.markets.home.tokenId;
        if (awayToken) map.set(`asset:${awayToken}`, game.key);
        if (homeToken) map.set(`asset:${homeToken}`, game.key);
      }
    }
    return map;
  }, [moneylineGamesForView]);

  // Primary click on odds cells → toggle row expansion (not navigate away)
  const handleNavigateToMarket = useCallback(
    (marketTicker: string) => {
      const gameKey = tickerToGameKey.get(marketTicker);
      if (gameKey) {
        handleToggleExpand(gameKey);
      }
    },
    [tickerToGameKey, handleToggleExpand]
  );

  // Max liquidity calculation for moneyline view (all sports)
  const maxLiquidity = useMemo(() => {
    if (!useMoneylineView) return 1000;

    const isTakerMode = oddsMode === 'taker';
    let max = 1000;
    for (const g of moneylineGamesForView) {
      const awayK = getTeamWinData({
        game: g.kalshi ?? null,
        venue: 'kalshi',
        teamSide: 'away',
        isTakerMode,
      });
      const homeK = getTeamWinData({
        game: g.kalshi ?? null,
        venue: 'kalshi',
        teamSide: 'home',
        isTakerMode,
      });
      const awayP = getTeamWinData({
        game: g.polymarket ?? null,
        venue: 'polymarket',
        teamSide: 'away',
        isTakerMode,
      });
      const homeP = getTeamWinData({
        game: g.polymarket ?? null,
        venue: 'polymarket',
        teamSide: 'home',
        isTakerMode,
      });
      const a = liquidityDollars(awayK.liq, awayK.priceCents) ?? 0;
      const h = liquidityDollars(homeK.liq, homeK.priceCents) ?? 0;
      const ap = liquidityDollars(awayP.liq, awayP.priceCents) ?? 0;
      const hp = liquidityDollars(homeP.liq, homeP.priceCents) ?? 0;
      max = Math.max(max, a, h, ap, hp);
    }
    return max;
  }, [moneylineGamesForView, oddsMode, useMoneylineView]);

  // Orderbook mapping for positions panel (legacy panels only)
  const _orderbookByTicker = useMemo(() => {
    const result: Record<
      string,
      {
        yesBestBid: number | null;
        yesBestAsk: number | null;
        noBestBid: number | null;
        noBestAsk: number | null;
      }
    > = {};

    for (const game of consolidatedGames) {
      if (game.kalshi) {
        const markets = [game.kalshi.markets.away, game.kalshi.markets.home];
        for (const market of markets) {
          const yesBestBid = market.yes[0]?.priceCents ?? null;
          const noBestBid = market.no[0]?.priceCents ?? null;
          const yesBestAsk = noBestBid !== null ? 100 - noBestBid : null;
          const noBestAsk = yesBestBid !== null ? 100 - yesBestBid : null;
          result[market.marketTicker] = { yesBestBid, yesBestAsk, noBestBid, noBestAsk };
        }
      }
    }

    return result;
  }, [consolidatedGames]);

  // Stable structural fingerprint for sportsGames — only changes when game
  // structure (keys, team codes, token IDs) changes, NOT on price updates.
  // This prevents polyTokenLabels from recomputing on every WS tick.
  const sportsGamesRef = useRef(sportsGames);
  sportsGamesRef.current = sportsGames;

  // Stable structural fingerprint for sportsGames — only changes when game
  // structure (event tickers, team codes) changes, NOT on price updates.
  // This prevents polyTokenLabels from recomputing on every WS tick.
  const sportsGamesStructureKey = useMemo(() => {
    const parts: string[] = [];
    for (const [key, games] of sportsGames) {
      parts.push(key);
      for (const g of games) {
        parts.push(g.eventTicker);
      }
    }
    return parts.join('|');
  }, [sportsGames]);

  // Build Polymarket tokenId -> human-readable label mapping.
  // Uses sportsGamesRef so the dependency is on sportsGamesStructureKey (stable)
  // rather than the sportsGames Map object (changes identity on every price update).
  const _polyTokenLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    const sg = sportsGamesRef.current;

    // Moneylines from consolidatedGames
    for (const game of consolidatedGames) {
      if (!game.polymarket) continue;
      const away = game.awayCode;
      const home = game.homeCode;
      const awayToken = game.polymarket.markets.away.tokenId;
      const homeToken = game.polymarket.markets.home.tokenId;
      if (awayToken) labels[awayToken] = `${away} ML`;
      if (homeToken) labels[homeToken] = `${home} ML`;
    }

    // Moneylines, spreads, totals from sportsGames
    for (const [, games] of sg) {
      for (const game of games) {
        const away = game.awayCode;
        const home = game.homeCode;

        if (game.marketType === 'moneyline') {
          const ml = game as MoneylineGameData;
          const awayToken = ml.polymarket?.markets.away.tokenId;
          const homeToken = ml.polymarket?.markets.home.tokenId;
          if (awayToken) labels[awayToken] = `${away} ML`;
          if (homeToken) labels[homeToken] = `${home} ML`;
        }

        if (game.marketType === 'spread') {
          const sd = game as SpreadGameData;
          if (sd.polymarketMain) {
            const favCode = sd.polymarketMain.favoriteSide === 'away' ? away : home;
            const dogCode = sd.polymarketMain.favoriteSide === 'away' ? home : away;
            const line = sd.polymarketMain.line;
            const favToken = sd.polymarketMain.markets.favorite.tokenId;
            const dogToken = sd.polymarketMain.markets.underdog.tokenId;
            if (favToken) labels[favToken] = `${favCode} -${line}`;
            if (dogToken) labels[dogToken] = `${dogCode} +${line}`;
          }
        }

        if (game.marketType === 'total') {
          const td = game as TotalGameData;
          if (td.polymarketMain) {
            const line = td.polymarketMain.line;
            const overToken = td.polymarketMain.markets.over.tokenId;
            const underToken = td.polymarketMain.markets.under.tokenId;
            if (overToken) labels[overToken] = `${away}@${home} O${line}`;
            if (underToken) labels[underToken] = `${away}@${home} U${line}`;
          }
        }
      }
    }

    return labels;
  }, [consolidatedGames, sportsGamesStructureKey]); // sportsGamesRef is stable, only recompute when structure changes

  // Trade modal request
  const placeOrderRequest: PlaceOrderRequest | null = useMemo(() => {
    if (!tradeReq) return null;
    return {
      venue: tradeReq.venue === 'polymarket' ? 'polymarket' : 'kalshi',
      marketTicker: tradeReq.marketTicker,
      tokenId: tradeReq.tokenId,
      conditionId: tradeReq.conditionId,
      tickSize: tradeReq.tickSize,
      negRisk: tradeReq.negRisk,
      side: tradeReq.side,
      priceCents: tradeReq.priceCents,
      isTakerMode: tradeReq.isTakerMode,
      teamLabel: tradeReq.teamLabel,
      gameDate: tradeReq.gameDate,
      startTimePt: tradeReq.startTimePt,
      defaultContracts: tradeReq.contracts,
      sweepContext: tradeReq.sweepContext,
    };
  }, [tradeReq]);

  const handleSubmitTrade = useCallback(
    (submission: PlaceOrderSubmission) => {
      if (!tradeReq) return;

      // Close modal immediately — non-blocking order entry
      const req = tradeReq;
      setTradeReq(null);
      setTradeError(null);

      const displayName = resolveDisplayTitle(req.marketTicker);
      const orderDesc = `${submission.contracts} ${req.side.toUpperCase()} @ ${submission.priceCents}\u00A2 on ${displayName}`;

      // Fire-and-forget: async API call runs in background
      // Toast notifications for success/failure + ActivityContext events (in useOrderManagement)
      if (req.venue === 'kalshi') {
        if (!onPlaceKalshiOrder) return;
        onPlaceKalshiOrder({
          marketTicker: req.marketTicker,
          side: req.side,
          priceCents: submission.priceCents,
          contracts: submission.contracts,
          isTakerMode: req.isTakerMode,
          expirationTs: submission.expirationTs,
        })
          .then(() => {
            addToast({ type: 'success', title: 'Order Placed', message: orderDesc });
          })
          .catch((err) => {
            const msg = err instanceof Error ? err.message : String(err);
            addToast({ type: 'error', title: 'Order Failed', message: msg, duration: 0 });
          });
      } else {
        if (!onPlacePolymarketOrder || !req.tokenId) return;
        onPlacePolymarketOrder({
          tokenId: req.tokenId,
          conditionId: req.conditionId,
          action: 'buy',
          priceCents: submission.priceCents,
          contracts: submission.contracts,
          isTakerMode: req.isTakerMode,
          tickSize: req.tickSize,
          negRisk: req.negRisk,
        })
          .then(() => {
            addToast({ type: 'success', title: 'Order Placed', message: orderDesc });
          })
          .catch((err) => {
            const msg = err instanceof Error ? err.message : String(err);
            addToast({ type: 'error', title: 'Order Failed', message: msg, duration: 0 });
          });
      }
    },
    [tradeReq, onPlaceKalshiOrder, onPlacePolymarketOrder, addToast]
  );

  // Selected game for the right-side detail panel
  const selectedGameKey = useMemo(() => {
    const keys = Array.from(expandedGameKeys);
    return keys[0] ?? null;
  }, [expandedGameKeys]);

  const selectedGame = useMemo(() => {
    if (!selectedGameKey) return null;
    return moneylineGamesForView.find((g) => g.key === selectedGameKey) ?? null;
  }, [selectedGameKey, moneylineGamesForView]);

  return (
    <div className={cn('space-y-6 p-4', feedStale && 'feed-stale')}>
      {/* Single row with all controls */}
      <DashboardHeader
        oddsMode={oddsMode}
        viewMode={viewMode}
        onModeChange={handleModeChange}
        onViewModeChange={handleViewModeChange}
        volumeWindows={volumeWindows}
        onToggleVolumeWindow={handleToggleVolumeWindow}
        leftContent={
          <>
            {/* Sport tabs */}
            <div className="bg-muted flex rounded-lg p-1">
              {SPORT_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleSportChange(tab.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    selectedSport === tab.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* Market type tabs */}
            <div className="bg-muted flex rounded-lg p-1">
              {availableMarketTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => handleMarketTypeChange(type)}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    selectedMarketType === type
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {MARKET_TYPE_LABELS[type]}
                </button>
              ))}
            </div>

            {/* Date navigator */}
            <div className="border-border flex items-center gap-1 rounded border px-1 py-0.5">
              <button
                onClick={handleDatePrev}
                disabled={!selectedDate || availableDates.indexOf(selectedDate) <= 0}
                className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors disabled:opacity-30"
                title="Previous date"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="date"
                value={selectedDate ?? ''}
                onChange={(e) => setSelectedDate(e.target.value || null)}
                className="text-foreground w-[7rem] cursor-pointer border-none bg-transparent px-1 py-0.5 text-xs font-medium outline-none [color-scheme:dark]"
                title="Click to pick a date"
              />
              <button
                onClick={handleDateNext}
                disabled={
                  !selectedDate || availableDates.indexOf(selectedDate) >= availableDates.length - 1
                }
                className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors disabled:opacity-30"
                title="Next date"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={handleDateToday}
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                  selectedDate === getTodayStr()
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title="Jump to today"
              >
                Today
              </button>
              <button
                onClick={handleDateAll}
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                  selectedDate === null
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title="Show all dates"
              >
                All
              </button>
              <span className="bg-border mx-1 inline-block h-4 w-px" />
              {(['active', 'all', 'settled'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setGameFilter(mode)}
                  className={cn(
                    'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                    gameFilter === mode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={
                    mode === 'active'
                      ? 'Show only active/upcoming games'
                      : mode === 'all'
                        ? 'Show all games'
                        : 'Show only settled/completed games'
                  }
                >
                  {mode === 'active' ? 'Active' : mode === 'all' ? 'All' : 'Settled'}
                </button>
              ))}
            </div>
          </>
        }
      />

      {/* Main content: table + optional right-side detail panel */}
      <div className="flex gap-4">
        {/* Table area (grows to fill available space) */}
        <div className="min-w-0 flex-1">
          {/* Moneyline View */}
          {useMoneylineView &&
            (viewMode === 'two-row' ? (
              <TwoRowMoneylines
                games={moneylineGamesForView}
                isLoading={moneylineLoading}
                loadingState={currentLoadingState}
                oddsMode={oddsMode}
                maxLiquidity={maxLiquidity}
                expandedGameKeys={expandedGameKeys}
                onToggleExpand={handleToggleExpand}
                onTradeClick={(req) => {
                  setTradeError(null);
                  setTradeReq(req);
                }}
                onNavigateToMarket={handleNavigateToMarket}
                sport={selectedSport}
                collapsed={tableCollapsed}
                onToggleCollapsed={handleToggleTableCollapsed}
                compact={tableCompact}
                onToggleCompact={handleToggleTableCompact}
              />
            ) : (
              <ConsolidatedMoneylines
                games={moneylineGamesForView}
                isLoading={moneylineLoading}
                loadingState={currentLoadingState}
                oddsMode={oddsMode}
                maxLiquidity={maxLiquidity}
                expandedGameKeys={expandedGameKeys}
                onToggleExpand={handleToggleExpand}
                onTradeClick={(req) => {
                  setTradeError(null);
                  setTradeReq(req);
                }}
                onNavigateToMarket={handleNavigateToMarket}
                sport={selectedSport}
                collapsed={tableCollapsed}
                onToggleCollapsed={handleToggleTableCollapsed}
                compact={tableCompact}
                onToggleCompact={handleToggleTableCompact}
              />
            ))}

          {/* Totals view */}
          {selectedMarketType === 'total' && (
            <TwoRowTotals
              sport={selectedSport}
              games={filteredCurrentGames as TotalGameData[]}
              isLoading={currentLoading}
              loadingState={currentLoadingState}
              volumeWindows={volumeWindows}
              onTradeClick={(req) => {
                setTradeError(null);
                setTradeReq(req);
              }}
              onNavigateToMarket={handleNavigateToMarket}
            />
          )}

          {/* Spreads view */}
          {selectedMarketType === 'spread' && (
            <TwoRowSpreads
              sport={selectedSport}
              games={filteredCurrentGames as SpreadGameData[]}
              isLoading={currentLoading}
              loadingState={currentLoadingState}
              volumeWindows={volumeWindows}
              onTradeClick={(req) => {
                setTradeError(null);
                setTradeReq(req);
              }}
              onNavigateToMarket={handleNavigateToMarket}
            />
          )}
        </div>

        {/* Right-side game detail panel */}
        {selectedGame && useMoneylineView && (
          <GameDetailPanel
            game={selectedGame}
            oddsMode={oddsMode}
            onTradeClick={(req) => {
              setTradeError(null);
              setTradeReq(req);
            }}
            onClose={() => setExpandedGameKeys(new Set())}
          />
        )}
      </div>

      {/* Game Accordion — grouped exposure view (replaces legacy panels) */}
      <GameAccordion
        orders={effectiveOrders}
        positions={effectivePositions}
        positionTitleByTicker={effectiveTitles}
        positionGameDateByTicker={effectiveDates}
        positionOutcomeByTicker={effectiveOutcomes}
        consolidatedGames={effectiveConsolidated}
        sportsGames={effectiveSportsGames}
        onCancelOrder={handleCancelOrder}
      />

      <LegacyOpenPositionsPanel
        positions={positions ?? []}
        markets={_markets}
        titleByTicker={positionTitleByTicker}
        gameDateByTicker={positionGameDateByTicker}
        outcomeByTicker={positionOutcomeByTicker}
      />
      <LegacyOpenOrdersPanel
        kalshiOrders={_orders ?? []}
        polyOrders={_polyOpenOrders}
        polyStatus={_polyStatus}
        titleByTicker={positionTitleByTicker}
        polyTokenLabels={_polyTokenLabels}
        outcomeByTicker={positionOutcomeByTicker}
        velocityByOrderId={_velocityByOrderId}
        absoluteQueueByOrderId={_absoluteQueueByOrderId}
        positions={positions}
        onCancelOrder={handleCancelOrder}
        onDecreaseOrder={_handleDecreaseOrder}
        onKillAll={_handleKillAll}
        onCancelPolyOrder={_handleCancelPolyOrder}
        onKillAllPolyOrders={_handleKillAllPolyOrders}
      />
      <LegacyCombinedOrdersPositionsPanel
        kalshiOrders={_orders ?? []}
        positions={positions ?? []}
        titleByTicker={positionTitleByTicker}
        gameDateByTicker={positionGameDateByTicker}
        outcomeByTicker={positionOutcomeByTicker}
        orderbookByTicker={_orderbookByTicker}
        onCancelOrder={handleCancelOrder}
        onKillAll={_handleKillAll}
      />

      {/* Trade Modal */}
      <PlaceOrderModal
        isOpen={tradeReq !== null}
        request={placeOrderRequest}
        submitting={false}
        error={tradeError}
        onClose={() => setTradeReq(null)}
        onSubmit={handleSubmitTrade}
      />
    </div>
  );
}

export default ValueDashboardView;

/** @forge-footer
 * @checklist
 *   1. DATE FILTER | selectedDate null = "all dates" | snap guard skips while loading
 *   2. SPORT SWITCH | market type auto-resets if invalid for new sport
 *   3. TRADE MODAL | tradeReq drives PlaceOrderModal — null = closed
 *   4. TEST DATA | effectiveX merges mock data when testData=true — check Map key collisions
 * @sync-group  none
 * @backlog     F-013: hide completed events (done) | B-001: +1h timezone offset (open)
 */
